/* eslint-disable react/forbid-elements */
import { type FC, type ReactNode, useCallback, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import { defineMessages, useIntl } from 'dibs-react-intl';
import CloseIcon from 'dibs-icons/exports/Close.ts';
import { useMenu } from '../useMenu/index.ts';
import { type PlacementSide, type PopoverAlignment } from './popoverPositioning.ts';
import { useAnchoredPopover } from './useAnchoredPopover.tsx';
import { useMenuCloseOnBlur } from './useMenuCloseOnBlur.ts';
import { PopoverTriangle } from './PopoverTriangle.tsx';
import styles from './Popover.module.css';

type BgKeys = Extract<keyof typeof dibsCss, `bg${string}`>;
type PopoverColor = BgKeys extends `bg${infer Name}` ? Uncapitalize<Name> : never;

const messages = defineMessages({
    closeButton: {
        id: 'Popover.closeButton',
        defaultMessage: 'Close',
    },
});

export type PopoverProps = {
    /**
     * Anchor edge the popover attaches to. At mobile breakpoints, sides are reduced to `top` unless
     * `side` is `bottom`.
     */
    side?: PlacementSide;
    /**
     * Alignment along the anchor edge. On mobile breakpoint widths, this is forced to `center`
     * regardless of the prop (pairs with the mobile `side` rule above).
     */
    align?: PopoverAlignment;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    anchorEl: HTMLElement | null;
    children: ReactNode;
    /** small=270px, medium=324px, large=450px */
    width?: 'small' | 'medium' | 'large' | 'auto';
    /** none=0px, xSmall=9px, smaller=14px, small=18px */
    padding?: 'none' | 'xSmall' | 'smaller' | 'small';
    /** none=0px, xxSmall=5px, xSmall=9px */
    borderRadius?: 'none' | 'xxSmall' | 'xSmall';
    bgColor?: PopoverColor;
    shadow?: boolean;
    showTriangle?: boolean;
    zIndex?: 'bump' | 'low' | 'middle' | 'high' | 'aboveNav';
    /** `auto` enables light-dismiss (outside pointer down + Escape). `manual` requires explicit close. */
    dismiss?: 'auto' | 'manual';
    /** `dialog` gets native dialog semantics. `div` for non-semantic overlays. */
    type?: 'dialog' | 'div';
    /**
     * ARIA role for the popover. `menu` enables `useMenu` keyboard navigation
     * (arrow keys, Escape, focus trapping) and forces the rendered element to a
     * `div` since `<dialog>` is semantically incompatible with `role="menu"`.
     * Children should be focusable elements with `role="menuitem"`.
     */
    role?: 'dialog' | 'menu';
    /** Only used when `role='menu'`. Closes the menu when focus leaves it. */
    closeOnBlur?: boolean;
    showCloseButton?: boolean;
    id?: string;
    /**
     * Plays a reveal animation (slide up + fade in) on the popover surface.
     */
    showRevealAnimation?: boolean;
    /** Delays the reveal animation by the given milliseconds. Only applies when `showRevealAnimation` is true. */
    revealAnimationDelayMs?: number;
    /**
     * Renders the popover into `document.body` via a React portal so it escapes any ancestor
     * stacking context.
     */
    portal?: boolean;
};

export const Popover: FC<PopoverProps> = ({
    dismiss = 'auto',
    side = 'top',
    align = 'center',
    open,
    onOpenChange,
    anchorEl,
    children,
    width = 'auto',
    padding = 'small',
    borderRadius = 'xxSmall',
    bgColor = 'white',
    zIndex = 'bump',
    shadow = true,
    showTriangle = true,
    showCloseButton,
    type: Tag = 'dialog',
    role = 'dialog',
    closeOnBlur,
    id,
    showRevealAnimation,
    revealAnimationDelayMs,
    portal,
}) => {
    const intl = useIntl();

    const generatedId = useId();
    const popoverId = id ?? generatedId;

    const isMenu = role === 'menu';
    // role="menu" is incompatible with <dialog> semantics, force div
    const ResolvedTag = isMenu ? 'div' : Tag;
    let ariaRole: 'menu' | 'dialog' | undefined;

    if (isMenu) {
        ariaRole = 'menu';
    } else if (ResolvedTag === 'div') {
        ariaRole = 'dialog';
    }

    // set aria attributes on the anchor here to simplify connecting anchor to popover
    useEffect(() => {
        if (!anchorEl) {
            return undefined;
        }
        anchorEl.setAttribute('aria-haspopup', role);
        anchorEl.setAttribute('aria-expanded', String(open));
        anchorEl.setAttribute('aria-controls', popoverId);
        return () => {
            anchorEl.removeAttribute('aria-haspopup');
            anchorEl.removeAttribute('aria-expanded');
            anchorEl.removeAttribute('aria-controls');
        };
    }, [anchorEl, open, popoverId, role]);

    // get position for popover and display tip triangle
    const { resolvedSide, triangleOffset, popoverRef } = useAnchoredPopover({
        side,
        align,
        dismiss,
        open,
        onOpenChange,
        anchorEl,
        autoFocus: !isMenu,
    });

    const handleMenuClose = useCallback(() => onOpenChange(false), [onOpenChange]);
    // Suppress useMenu's own focusout handling. Popover owns close-on-blur so
    // it can ignore focus moving to the trigger (which would otherwise race
    // with the trigger's click handler and cause the menu to reopen).
    const menuRef = useMenu<HTMLElement | null>(isMenu && open, handleMenuClose, false);
    const setRef = useCallback(
        (node: (HTMLDivElement & HTMLDialogElement) | null) => {
            popoverRef.current = node;

            if (isMenu) {
                menuRef(node);
            }
        },
        [popoverRef, menuRef, isMenu]
    );

    useMenuCloseOnBlur({
        enabled: isMenu && open && closeOnBlur !== false,
        popoverRef,
        anchorEl,
        onClose: handleMenuClose,
    });

    const capitalColor = (bgColor.charAt(0).toUpperCase() + bgColor.slice(1)) as Capitalize<
        typeof bgColor
    >;
    const bgKey = `bg${capitalColor}` as const;
    const fillKey = `fill${capitalColor}` as const;

    const visualClassName = classnames(dibsCss[bgKey], dibsCss[fillKey], {
        [dibsCss.pNone]: padding === 'none',
        [dibsCss.pXsmall]: padding === 'xSmall',
        [dibsCss.pSmall]: padding === 'small',
        [dibsCss.pSmaller]: padding === 'smaller',
        [dibsCss.roundedNone]: borderRadius === 'none',
        [dibsCss.roundedXxsmall]: borderRadius === 'xxSmall',
        [dibsCss.roundedXsmall]: borderRadius === 'xSmall',
        [styles.shadow]: shadow,
    });

    const positioningClassName = classnames(
        dibsCss.borderNone,
        dibsCss.overflowVisible,
        dibsCss.fixed,
        dibsCss.insetAuto,
        dibsCss.m0,
        styles.surface,
        {
            [dibsCss.wMax]: width === 'auto',
            [styles.small]: width === 'small',
            [styles.medium]: width === 'medium',
            [styles.large]: width === 'large',
            [dibsCss.zBump]: zIndex === 'bump',
            [dibsCss.zLow]: zIndex === 'low',
            [dibsCss.zAboveNav]: zIndex === 'aboveNav',
            [dibsCss.zMiddle]: zIndex === 'middle',
            [dibsCss.zHigh]: zIndex === 'high',
            [dibsCss.pNone]: showRevealAnimation,
            [dibsCss.bgTransparent]: showRevealAnimation,
        }
    );

    const surfaceClassName = showRevealAnimation
        ? positioningClassName
        : classnames(positioningClassName, visualClassName);

    const content = (
        <>
            {showCloseButton && (
                <button
                    type="button"
                    onClick={() => {
                        onOpenChange(false);
                        anchorEl?.focus();
                    }}
                    aria-label={intl.formatMessage(messages.closeButton)}
                    className={classnames(
                        styles.popoverHideButton,
                        dibsCss.w20px,
                        dibsCss.h20px,
                        dibsCss.cursorPointer,
                        dibsCss.absolute,
                        dibsCss.pNone,
                        dibsCss.roundedNone,
                        dibsCss.bgTransparent,
                        dibsCss.borderNone,
                        dibsCss.textNoir,
                        dibsCss.fillNoir
                    )}
                >
                    <CloseIcon variation="20" />
                </button>
            )}
            {showTriangle && <PopoverTriangle side={resolvedSide} offset={triangleOffset} />}
            {children}
        </>
    );

    const popoverNode = (
        <ResolvedTag
            ref={setRef}
            id={popoverId}
            className={surfaceClassName}
            open={ResolvedTag === 'dialog' ? open : undefined}
            role={ariaRole}
            {...{ 'data-state': open ? 'open' : 'closed' }}
        >
            {showRevealAnimation ? (
                <div
                    className={classnames(visualClassName, dibsCss.relative, styles.reveal)}
                    style={
                        revealAnimationDelayMs
                            ? { animationDelay: `${revealAnimationDelayMs}ms` }
                            : undefined
                    }
                >
                    {content}
                </div>
            ) : (
                content
            )}
        </ResolvedTag>
    );

    if (portal && typeof document !== 'undefined') {
        return createPortal(popoverNode, document.body);
    }

    return popoverNode;
};

export type { PlacementSide, PopoverAlignment } from './popoverPositioning.ts';
